import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useOutletContext, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, ChevronLeft, ChevronRight, ImageOff, Map as MapIcon, MapPin, X } from "lucide-react";

import SEO from "@/components/SEO";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import SafeImage from "@/components/SafeImage";
import {
  SIGNBOARDS,
  REGIONS,
  getSignboardBySlug,
  buildStreetViewEmbedSrc,
} from "@/data/signboards";

/**
 * Signboard detail page — one signboard, full photo gallery, and a
 * sticky left-rail nav that lists every other signboard grouped by
 * region. Owner directive: visitors should be able to hop between
 * detail pages without going back to the hub.
 *
 * Mobile fallback: the left-rail collapses into a horizontal pill
 * scroller above the main content. Same data, mobile-first ergonomics.
 */
export default function SignboardDetailPage() {
  const { lang } = useOutletContext();
  const { slug } = useParams();
  const isJa = lang === "ja";

  const board = getSignboardBySlug(slug);

  // Lightbox state — index of the photo currently expanded, or null when closed.
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const photoCount = board?.photos?.length || 0;
  const isLightboxOpen = lightboxIdx !== null;

  // Focus management for the modal dialog (ARIA Authoring Practices —
  // modal dialog pattern): remember the gallery photo that opened the
  // lightbox so focus can be restored to it on close, and give the
  // dialog itself a ref so we can move focus in on open + trap Tab
  // inside it while open.
  const lightboxTriggerRef = useRef(null);
  const lightboxContainerRef = useRef(null);

  const closeLightbox = useCallback(() => setLightboxIdx(null), []);
  const nextPhoto = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i + 1) % photoCount));
  }, [photoCount]);
  const prevPhoto = useCallback(() => {
    setLightboxIdx((i) => (i === null ? null : (i - 1 + photoCount) % photoCount));
  }, [photoCount]);

  // Body scroll lock + focus-in-on-open / focus-restore-on-close.
  // Keyed on isLightboxOpen (not lightboxIdx) so it only runs on the
  // open/close transition, not on every prev/next photo navigation.
  useEffect(() => {
    if (!isLightboxOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const triggerEl = lightboxTriggerRef.current;
    const raf = requestAnimationFrame(() => {
      lightboxContainerRef.current?.querySelector('[data-testid="lightbox-close"]')?.focus();
    });
    return () => {
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(raf);
      if (triggerEl && typeof triggerEl.focus === "function") triggerEl.focus();
    };
  }, [isLightboxOpen]);

  // Keyboard controls while open: Escape closes, arrows navigate, Tab
  // is trapped inside the dialog's focusable buttons (close/prev/next).
  useEffect(() => {
    if (!isLightboxOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        closeLightbox();
        return;
      }
      if (e.key === "ArrowRight") nextPhoto();
      if (e.key === "ArrowLeft") prevPhoto();
      if (e.key === "Tab") {
        const container = lightboxContainerRef.current;
        if (!container) return;
        const focusable = Array.from(container.querySelectorAll("button")).filter((el) => !el.disabled);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isLightboxOpen, closeLightbox, nextPhoto, prevPhoto]);

  // Reset scroll when switching boards (route param changes but page
  // doesn't unmount because the parent layout is the same).
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setLightboxIdx(null);
  }, [slug]);

  if (!board) {
    return <Navigate to={`/${lang}/signboards`} replace />;
  }

  const region = REGIONS.find((r) => r.key === board.region);
  const accent = region?.accent || "#0F1D2D";
  const streetViewEmbedSrc = buildStreetViewEmbedSrc(board.streetView);

  // Group all signboards by region for the left-rail nav.
  const byRegion = REGIONS.map((r) => ({
    region: r,
    boards: SIGNBOARDS.filter((s) => s.region === r.key),
  })).filter((g) => g.boards.length > 0);

  return (
    <div data-testid="signboard-detail-page" className="bg-white">
      <SEO
        title={
          isJa
            ? `${board.title.ja}｜JapanETCcard看板掲出 | NoJo Enterprise`
            : `${board.title.en} | JapanETCcard Signboards | NoJo Enterprise`
        }
        description={board.summary[lang]}
        path={`/${lang}/signboards/${board.slug}`}
        lang={lang}
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "看板掲出" : "Signboards", url: `/${lang}/signboards` },
          { name: isJa ? region?.ja : region?.en, url: `/${lang}/signboards` },
          { name: board.title[lang], url: `/${lang}/signboards/${board.slug}` },
        ])}
      />

      {/* ===== HEADER STRIP — single-line breadcrumb on every viewport ===== */}
      <section className="border-b border-[#E2E8F0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-6 md:pt-8 pb-5">
          <div className="flex flex-nowrap items-center gap-2 text-[12px] text-[#64748B] overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <Link
              to={`/${lang}/signboards`}
              data-testid="signboard-detail-back"
              className="inline-flex items-center gap-1.5 shrink-0 hover:text-[#0F1D2D] transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
              {isJa ? "看板一覧" : "All signboards"}
            </Link>
            <ChevronRight className="h-3 w-3 opacity-50 shrink-0" strokeWidth={2} />
            <span className="font-semibold shrink-0" style={{ color: accent }}>
              {isJa ? region?.ja : region?.en}
            </span>
            <ChevronRight className="h-3 w-3 opacity-50 shrink-0" strokeWidth={2} />
            <span className="text-[#0F1D2D] font-semibold shrink-0">{board.title[lang]}</span>
          </div>
        </div>
      </section>

      {/* ===== MOBILE RAIL (pill scroller) ===== */}
      <div
        data-testid="signboard-mobile-rail"
        className="md:hidden border-b border-[#E2E8F0] bg-[#F8F9FA] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="flex items-center gap-2 px-4 py-3 whitespace-nowrap">
          {/* Map shortcuts first */}
          <Link
            to={`/${lang}/signboards/map/mainland`}
            data-testid="signboard-mobile-rail-map-mainland"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold tracking-tight transition-colors shrink-0 bg-white border border-[#E2E8F0] text-[#475569] hover:text-[#0F1D2D]"
          >
            <MapIcon className="h-3 w-3" strokeWidth={2} style={{ color: "#0E5FB0" }} />
            {isJa ? "本州マップ" : "Mainland Map"}
          </Link>
          <Link
            to={`/${lang}/signboards/map/okinawa`}
            data-testid="signboard-mobile-rail-map-okinawa"
            className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold tracking-tight transition-colors shrink-0 bg-white border border-[#E2E8F0] text-[#475569] hover:text-[#0F1D2D]"
          >
            <MapIcon className="h-3 w-3" strokeWidth={2} style={{ color: "#2F8F3C" }} />
            {isJa ? "沖縄マップ" : "Okinawa Map"}
          </Link>
          {SIGNBOARDS.map((b) => {
            const active = b.slug === board.slug;
            const bAcc = REGIONS.find((r) => r.key === b.region)?.accent || "#0F1D2D";
            return (
              <Link
                key={b.slug}
                to={`/${lang}/signboards/${b.slug}`}
                data-testid={`mobile-rail-${b.slug}`}
                style={active ? { backgroundColor: bAcc, color: "#fff" } : undefined}
                className={[
                  "inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-semibold tracking-tight transition-colors shrink-0",
                  active
                    ? "shadow-sm"
                    : "bg-white border border-[#E2E8F0] text-[#475569] hover:text-[#0F1D2D]",
                ].join(" ")}
              >
                <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: active ? "#fff" : bAcc }} />
                {b.title[lang]}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ===== MAIN GRID: left rail + content ===== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-8 md:pt-12 pb-16 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          {/* LEFT RAIL (desktop only) */}
          <aside
            className="hidden md:block md:col-span-3"
            data-testid="signboard-left-rail"
          >
            <div className="sticky top-24">
              {/* Detailed maps shortcut */}
              <div className="text-[10px] tracking-[0.28em] font-mono font-bold text-[#64748B] uppercase mb-3">
                {isJa ? "詳細マップ" : "Detailed maps"}
              </div>
              <nav className="space-y-1 mb-7">
                <Link
                  to={`/${lang}/signboards/map/mainland`}
                  data-testid="signboard-left-rail-map-mainland"
                  className="block rounded-xl px-3 py-2.5 text-[13px] font-semibold tracking-tight text-[#0F1D2D] hover:bg-[#F1F5F9] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <MapIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} style={{ color: "#0E5FB0" }} />
                    <span className="truncate leading-tight">
                      {isJa ? "本州マップ" : "Mainland Map"}
                    </span>
                  </div>
                </Link>
                <Link
                  to={`/${lang}/signboards/map/okinawa`}
                  data-testid="signboard-left-rail-map-okinawa"
                  className="block rounded-xl px-3 py-2.5 text-[13px] font-semibold tracking-tight text-[#0F1D2D] hover:bg-[#F1F5F9] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <MapIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} style={{ color: "#2F8F3C" }} />
                    <span className="truncate leading-tight">
                      {isJa ? "沖縄マップ" : "Okinawa Map"}
                    </span>
                  </div>
                </Link>
              </nav>

              <div className="text-[10px] tracking-[0.28em] font-mono font-bold text-[#64748B] uppercase mb-3">
                {isJa ? "全看板" : "All signboards"}
              </div>
              <nav className="space-y-6">
                {byRegion.map(({ region: r, boards }) => (
                  <div key={r.key}>
                    <div
                      className="flex items-center gap-2 text-[11.5px] tracking-[0.18em] uppercase font-bold mb-2"
                      style={{ color: r.accent }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: r.accent }} />
                      {isJa ? r.ja : r.en}
                    </div>
                    <ul className="space-y-1">
                      {boards.map((b) => {
                        const active = b.slug === board.slug;
                        return (
                          <li key={b.slug}>
                            <Link
                              to={`/${lang}/signboards/${b.slug}`}
                              data-testid={`left-rail-${b.slug}`}
                              style={active ? { backgroundColor: r.accent } : undefined}
                              className={[
                                "group block rounded-xl px-3 py-2.5 text-[13px] font-semibold tracking-tight transition-colors",
                                active
                                  ? "text-white shadow-sm"
                                  : "text-[#0F1D2D] hover:bg-[#F1F5F9]",
                              ].join(" ")}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate leading-tight">{b.title[lang]}</span>
                                {b.route && (
                                  <span
                                    className={[
                                      "text-[9.5px] tracking-[0.12em] uppercase font-bold shrink-0",
                                      active ? "text-white/80" : "text-[#94A3B8]",
                                    ].join(" ")}
                                  >
                                    {b.route}
                                  </span>
                                )}
                              </div>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </nav>

              <Link
                to={`/${lang}/signboards`}
                data-testid="left-rail-hub"
                className="mt-7 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#5B2E83] hover:text-[#3F1E5C] transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
                {isJa ? "看板ハブに戻る" : "Back to signboards hub"}
              </Link>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <article className="md:col-span-9 min-w-0">
            {/* ===== STREET VIEW HERO ===== */}
            {board.streetView && streetViewEmbedSrc ? (
              <div
                data-testid="signboard-streetview-hero"
                className="mb-8 rounded-3xl overflow-hidden ring-1 ring-[#E2E8F0] bg-[#5B2E83] shadow-[0_24px_60px_-24px_rgba(91,46,131,0.4)]"
              >
                {/* Top label band */}
                <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 bg-[#5B2E83] text-white">
                  <span className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-bold">
                    <span className="relative inline-flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[#7AB8E8] opacity-60 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[#7AB8E8]" />
                    </span>
                    {board.streetView.capturedLabel
                      ? board.streetView.capturedLabel[lang]
                      : isJa
                      ? "ライブ · Google ストリートビュー"
                      : "Live · Google Street View"}
                  </span>
                  <a
                    href={board.streetView.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="signboard-streetview-open"
                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 ring-1 ring-white/15 text-white text-[11.5px] font-semibold tracking-tight transition-colors"
                  >
                    {isJa ? "Google マップで開く" : "Open in Google Maps"}
                    <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </a>
                </div>
                {/* Live embed */}
                <iframe
                  data-testid="signboard-streetview-iframe"
                  src={streetViewEmbedSrc}
                  title={
                    isJa
                      ? `${board.title.ja} のGoogle ストリートビュー`
                      : `Google Street View of ${board.title.en}`
                  }
                  className="w-full block aspect-[16/9] md:aspect-[21/9]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            ) : (
              <div
                data-testid="signboard-streetview-pending"
                className="mb-8 rounded-3xl overflow-hidden ring-1 ring-dashed ring-[#CBD5E1] bg-[#F8F9FA]"
              >
                <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3 bg-white border-b border-[#E2E8F0]">
                  <span className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#475569]">
                    <span className="h-2 w-2 rounded-full bg-[#CBD5E1]" />
                    {isJa ? "Google ストリートビュー" : "Google Street View"}
                  </span>
                  <span className="text-[10.5px] tracking-[0.22em] uppercase font-bold text-[#94A3B8]">
                    {isJa ? "準備中" : "Coming soon"}
                  </span>
                </div>
                <div className="aspect-[16/9] md:aspect-[21/9] flex flex-col items-center justify-center text-center px-6">
                  <span className="inline-flex h-10 w-10 rounded-full bg-white items-center justify-center ring-1 ring-[#E2E8F0] mb-3">
                    <ArrowUpRight className="h-4 w-4 text-[#5B2E83]" strokeWidth={2} />
                  </span>
                  <p className="text-[13.5px] font-semibold text-[#0F1D2D] max-w-md leading-snug">
                    {isJa
                      ? "Google ストリートビューのリンクは近日中に追加予定です。"
                      : "A live Google Street View embed will appear here once we add the link for this signboard."}
                  </p>
                </div>
              </div>
            )}

            {/* Title block */}
            <div className="flex items-center gap-2.5 mb-4">
              <span
                className="inline-flex items-center gap-1.5 text-[10.5px] tracking-[0.2em] uppercase font-bold text-white px-2.5 py-1 rounded-full"
                style={{ backgroundColor: accent }}
                data-testid="signboard-region-pill"
              >
                {isJa ? region?.ja : region?.en}
              </span>
              {board.route && (
                <span className="inline-flex items-center text-[10.5px] tracking-[0.2em] uppercase font-bold text-[#0F1D2D] bg-[#F1F5F9] px-2.5 py-1 rounded-full">
                  {board.route}
                </span>
              )}
              <span className="inline-flex items-center text-[10.5px] tracking-[0.2em] uppercase font-bold text-[#2F8F3C] bg-[#F3FAEE] px-2.5 py-1 rounded-full">
                {isJa ? "稼働中" : "Live"}
              </span>
            </div>

            <h1
              data-testid="signboard-detail-title"
              className="text-3xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight text-[#0F1D2D] leading-[1.05]"
            >
              {board.title[lang]}
            </h1>

            <div className="mt-3 flex items-center gap-2 text-[13.5px] text-[#475569]">
              <MapPin className="h-4 w-4 shrink-0" strokeWidth={2} style={{ color: accent }} />
              <span>{board.location[lang]}</span>
              {board.nearest && (
                <>
                  <span className="text-[#CBD5E1]">·</span>
                  <span className="text-[#64748B]">{board.nearest[lang]}</span>
                </>
              )}
            </div>

            <p className="mt-5 text-[14.5px] md:text-[16px] leading-[1.75] text-[#334155] max-w-3xl">
              {board.summary[lang]}
            </p>

            {/* Quick stats */}
            <div className="mt-6 flex flex-wrap items-center gap-2.5">
              <Pill label={isJa ? "写真" : "Photos"} value={photoCount > 0 ? String(photoCount) : (isJa ? "準備中" : "Coming soon")} />
              <Pill label={isJa ? "路線" : "Route"} value={board.route || (isJa ? "本社" : "HQ")} />
              <Pill label={isJa ? "状況" : "Status"} value={isJa ? "稼働中" : "Live"} accent="#2F8F3C" />
            </div>

            {/* Artwork preview band removed — current signboard creative differs
                from the older artwork file and isn't shown here. The live
                Street View embed at the top of the page is the canonical
                "what does it look like in the real world" view. */}

            {/* Photo gallery */}
            <div className="mt-12">
              <div className="flex items-end justify-between gap-4 mb-5">
                <div>
                  <div className="text-[10.5px] tracking-[0.28em] font-mono font-bold text-[#0F1D2D] uppercase mb-2">
                    {isJa ? "現地ギャラリー" : "On-site gallery"}
                  </div>
                  <h2 className="text-2xl md:text-3xl font-display font-bold tracking-tight text-[#0F1D2D] leading-tight">
                    {isJa ? "現地から、すべての写真。" : "Every photo from the location."}
                  </h2>
                </div>
                {photoCount > 0 && (
                  <span className="text-[12px] text-[#64748B] font-semibold tabular-nums shrink-0">
                    {photoCount} {isJa ? "枚" : "photos"}
                  </span>
                )}
              </div>

              {photoCount === 0 ? (
                <div
                  data-testid="signboard-gallery-empty"
                  className="rounded-3xl bg-[#F8F9FA] border border-dashed border-[#CBD5E1] px-6 py-12 text-center"
                >
                  <ImageOff className="h-8 w-8 mx-auto text-[#94A3B8] mb-3" strokeWidth={1.6} />
                  <p className="text-[14px] font-semibold text-[#0F1D2D]">
                    {board.photosPending?.[lang] || (isJa ? "写真ギャラリーは準備中です。" : "Photo gallery being assembled.")}
                  </p>
                </div>
              ) : (
                <div
                  data-testid="signboard-gallery"
                  className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
                >
                  {board.photos.map((src, idx) => (
                    <button
                      type="button"
                      key={src}
                      onClick={(e) => {
                        lightboxTriggerRef.current = e.currentTarget;
                        setLightboxIdx(idx);
                      }}
                      className="group rounded-2xl overflow-hidden ring-1 ring-[#E2E8F0] bg-[#F8F9FA] aspect-[4/3] cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#5B2E83] hover:ring-[#0F1D2D]/30 transition-all"
                      data-testid={`gallery-photo-${idx}`}
                      aria-label={
                        isJa
                          ? `写真 ${idx + 1} を拡大表示`
                          : `Open photo ${idx + 1} in lightbox`
                      }
                    >
                      <SafeImage
                        src={src}
                        alt={`${board.title[lang]} — ${idx + 1}`}
                        imgClassName="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                        fallbackLabel={isJa ? "画像なし" : "Unavailable"}
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA band */}
            <div className="mt-14 rounded-3xl overflow-hidden bg-gradient-to-br from-[#5B2E83] via-[#4A2570] to-[#3F1F5B] text-white p-6 md:p-10 relative">
              <div className="absolute inset-x-0 top-0 flex h-1">
                <span className="flex-1 bg-[#5B2E83]" />
                <span className="flex-1 bg-[#2F8F3C]" />
                <span className="flex-1 bg-[#0E5FB0]" />
              </div>
              <div className="grid md:grid-cols-12 gap-6 items-center">
                <div className="md:col-span-8">
                  <div className="text-[10.5px] tracking-[0.28em] font-mono font-bold text-[#7AB8E8] uppercase mb-2">
                    {isJa ? "看板掲出について" : "About signboard placement"}
                  </div>
                  <h3 className="text-xl md:text-3xl font-display font-bold tracking-tight leading-tight">
                    {isJa
                      ? "看板掲出・地域パートナーシップのご相談はこちらから。"
                      : "Inquire about signboard placement or regional partnership."}
                  </h3>
                </div>
                <div className="md:col-span-4 flex flex-wrap gap-2.5 md:justify-end">
                  <Link
                    to={`/${lang}/contact`}
                    data-testid="signboard-cta-contact"
                    className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-[#78D33C] hover:bg-[#5FB825] text-white text-[13px] font-semibold tracking-tight transition-colors"
                  >
                    {isJa ? "お問い合わせ" : "Contact"}
                    <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
                  </Link>
                  <Link
                    to={`/${lang}/signboards`}
                    data-testid="signboard-cta-hub"
                    className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold tracking-tight transition-colors ring-1 ring-white/20"
                  >
                    {isJa ? "看板ハブ" : "All signboards"}
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* ===== LIGHTBOX ===== */}
      {lightboxIdx !== null && (
        <Lightbox
          containerRef={lightboxContainerRef}
          src={board.photos[lightboxIdx]}
          index={lightboxIdx}
          total={photoCount}
          alt={`${board.title[lang]} — ${lightboxIdx + 1}`}
          onClose={closeLightbox}
          onPrev={prevPhoto}
          onNext={nextPhoto}
          isJa={isJa}
        />
      )}
    </div>
  );
}

function Lightbox({ containerRef, src, index, total, alt, onClose, onPrev, onNext, isJa }) {
  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={isJa ? "写真の拡大表示" : "Photo lightbox"}
      data-testid="signboard-lightbox"
      className="fixed inset-0 z-[80] bg-[#2A1340]/95 backdrop-blur-sm flex items-center justify-center animate-brand-fade-in"
      onClick={onClose}
    >
      {/* Top bar */}
      <div
        className="absolute top-0 inset-x-0 px-5 md:px-8 py-4 flex items-center justify-between text-white"
        style={{ paddingTop: "max(env(safe-area-inset-top), 16px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[12px] tracking-[0.22em] uppercase font-mono font-bold text-white/80 tabular-nums">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="lightbox-close"
          aria-label={isJa ? "閉じる" : "Close"}
          className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 ring-1 ring-white/20 text-white transition-colors"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* Prev / Next — large, fixed to viewport edges so position is
          consistent regardless of image dimensions */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            data-testid="lightbox-prev"
            aria-label={isJa ? "前の写真" : "Previous photo"}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-16 w-16 md:h-20 md:w-20 rounded-full bg-white/15 hover:bg-white/30 active:bg-white/40 ring-1 ring-white/25 hover:ring-white/40 text-white transition-colors backdrop-blur-sm"
          >
            <ChevronLeft className="h-8 w-8 md:h-10 md:w-10" strokeWidth={2.2} />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            data-testid="lightbox-next"
            aria-label={isJa ? "次の写真" : "Next photo"}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-16 w-16 md:h-20 md:w-20 rounded-full bg-white/15 hover:bg-white/30 active:bg-white/40 ring-1 ring-white/25 hover:ring-white/40 text-white transition-colors backdrop-blur-sm"
          >
            <ChevronRight className="h-8 w-8 md:h-10 md:w-10" strokeWidth={2.2} />
          </button>
        </>
      )}

      {/* Image */}
      <div
        className="relative max-w-[92vw] max-h-[88vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={alt}
          className="max-w-[92vw] max-h-[88vh] object-contain rounded-lg shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
          onError={(e) => {
            e.currentTarget.style.display = "none";
            e.currentTarget.nextSibling.style.display = "flex";
          }}
        />
        <div
          className="hidden w-[min(80vw,420px)] h-[min(60vh,320px)] rounded-lg bg-white/10 ring-1 ring-white/15 items-center justify-center"
        >
          <div className="flex flex-col items-center gap-2 text-white/70">
            <ImageOff className="h-8 w-8" strokeWidth={1.6} aria-hidden />
            <span className="text-[11px] tracking-[0.16em] uppercase font-bold">
              {isJa ? "画像を読み込めません" : "Photo unavailable"}
            </span>
          </div>
        </div>
      </div>

      {/* Hint */}
      <div
        className="absolute bottom-4 inset-x-0 text-center text-[11px] tracking-[0.16em] uppercase font-bold text-white/60 px-4"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 8px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {isJa ? "ESC で閉じる · ← → で移動" : "ESC to close · ← → to navigate"}
      </div>
    </div>
  );
}

function Pill({ label, value, accent }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 rounded-full bg-[#F1F5F9] px-3 py-1.5">
      <span className="text-[9.5px] tracking-[0.18em] uppercase font-bold text-[#64748B]">{label}</span>
      <span
        className="text-[12.5px] font-bold tracking-tight tabular-nums"
        style={{ color: accent || "#0F1D2D" }}
      >
        {value}
      </span>
    </span>
  );
}
